import { flattenJSON } from '~/services/pipes/extra-context-keys.ts';

export const CONTEXT_SETTER_CONTEXT_STORAGE_KEY = 'zapp-context-setter:context';

export const CONTEXT_SETTER_REVALIDATE_STORAGE_PREFIX =
  'zapp-context-setter:revalidate';

export const CONTEXT_SETTER_RESOLVED_STORAGE_PREFIX =
  'zapp-context-setter:resolved';

export const CONTEXT_SETTER_READY_EVENT = 'zapp-context-setter:ready';

export const CLIENT_CONTEXT_QUERY_PARAM = 'client-context';

export type ClientContextKeys = Record<string, string | number | boolean>;

/**
 * Reads Context Setter keys stored under the single sessionStorage key.
 *
 * @returns Flat context keys, or an empty object when missing or invalid.
 */
export function getStoredContextSetterKeys(): ClientContextKeys {
  if (typeof window === 'undefined' || !window.sessionStorage) return {};

  try {
    const storedContextRaw: string | null = window.sessionStorage.getItem(
      CONTEXT_SETTER_CONTEXT_STORAGE_KEY
    );

    if (!storedContextRaw) return {};

    const parsedContext: unknown = JSON.parse(storedContextRaw);

    if (!parsedContext || typeof parsedContext !== 'object') return {};

    return Object.entries(parsedContext as Record<string, unknown>).reduce(
      (
        clientContextKeys: ClientContextKeys,
        [key, value]: [string, unknown]
      ) => {
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          clientContextKeys[key] = value;
        }

        return clientContextKeys;
      },
      {}
    );
  } catch {
    return {};
  }
}

/**
 * Whether Context Setter remote data is available in sessionStorage.
 *
 * @returns `true` when stored context keys exist.
 */
export function hasStoredContextSetterKeys(): boolean {
  return Object.keys(getStoredContextSetterKeys()).length > 0;
}

/**
 * Reads namespaced sessionStorage entries and flattens nested objects into context keys.
 * Colon keys are normalized to dots. Invalid JSON is kept as a raw string.
 * Context Setter storage is skipped; use {@link getStoredContextSetterKeys} for that.
 *
 * @returns Flat context keys, or an empty object when storage is missing or unreadable.
 */
export function extractStoredSessionContextKeys(): ClientContextKeys {
  if (typeof window === 'undefined' || !window.sessionStorage) return {};

  const extracted: ClientContextKeys = {};

  try {
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const storageKey: string | null = window.sessionStorage.key(index);

      if (!storageKey || isInternalSessionKey(storageKey)) continue;
      if (!isNamespacedContextKey(storageKey)) continue;

      const rawValue: string | null = window.sessionStorage.getItem(storageKey);

      if (rawValue == null) continue;

      const canonicalKey: string = toCanonicalContextKey(storageKey);
      const colonKeyAlreadySet: boolean =
        storageKey.includes(':') &&
        Object.prototype.hasOwnProperty.call(extracted, canonicalKey);

      if (colonKeyAlreadySet) continue;

      assignSessionContextValue(extracted, canonicalKey, rawValue);
    }
  } catch {
    return extracted;
  }

  return extracted;
}

/**
 * Merges dynamic session context keys with Context Setter keys.
 * Context Setter values win when the same key exists in both.
 *
 * @returns Flat context keys for client-feed requests.
 */
export function getMergedClientContextKeys(): ClientContextKeys {
  return {
    ...extractStoredSessionContextKeys(),
    ...getStoredContextSetterKeys(),
  };
}

/**
 * Whether a sessionStorage key belongs to Context Setter bookkeeping.
 *
 * @param storageKey - Raw sessionStorage key.
 * @returns `true` when the key must not be forwarded as session context.
 */
function isInternalSessionKey(storageKey: string): boolean {
  return (
    storageKey === CONTEXT_SETTER_CONTEXT_STORAGE_KEY ||
    storageKey.startsWith(CONTEXT_SETTER_REVALIDATE_STORAGE_PREFIX)
  );
}

const NAMESPACED_CONTEXT_KEY_PATTERN: RegExp = /^[\w-]+(?:[.:][\w-]+)+$/;

/**
 * Whether a sessionStorage key looks like `namespace:key` / `namespace.key`.
 * URL-shaped cache keys (`/`, `?`, `%`, `=`) and `ctx/...` keys never match.
 *
 * @param storageKey - Raw sessionStorage key.
 * @returns `true` for identifier-only dot or colon namespaced keys.
 */
function isNamespacedContextKey(storageKey: string): boolean {
  return NAMESPACED_CONTEXT_KEY_PATTERN.test(storageKey);
}

/**
 * Normalizes colon namespace separators to dots.
 *
 * @param storageKey - Raw sessionStorage key.
 * @returns Canonical dotted context key.
 */
function toCanonicalContextKey(storageKey: string): string {
  return storageKey.replace(/:/g, '.');
}

/**
 * Whether a parsed storage value can be sent as a context key.
 *
 * @param value - Parsed sessionStorage value.
 * @returns `true` for string, number, and boolean values.
 */
function isContextPrimitive(
  value: unknown
): value is string | number | boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

/**
 * Assigns one sessionStorage value onto flat context keys.
 * Nested objects are kept as JSON and also expanded to dotted primitive keys.
 *
 * @param target - Accumulator of context keys.
 * @param key - Canonical dotted key.
 * @param rawValue - Raw sessionStorage string.
 * @returns Nothing.
 */
function assignSessionContextValue(
  target: ClientContextKeys,
  key: string,
  rawValue: string
): void {
  let parsed: unknown;

  try {
    parsed = JSON.parse(rawValue);
  } catch {
    target[key] = rawValue;
    return;
  }

  if (isContextPrimitive(parsed)) {
    target[key] = parsed;
    return;
  }

  if (!parsed || typeof parsed !== 'object') return;

  target[key] = rawValue;

  if (Array.isArray(parsed)) return;

  const flattened: { [flattenedKey: string]: unknown } = flattenJSON(
    parsed as { [key: string]: unknown }
  );

  Object.entries(flattened).forEach(
    ([childKey, childValue]: [string, unknown]) => {
      if (!isContextPrimitive(childValue)) return;

      target[`${key}.${childKey}`] = childValue;
    }
  );
}

/**
 * Reads selected context keys directly from sessionStorage using configured key names.
 * Supports both dot and colon namespace separators for compatibility.
 *
 * @param keys - Context keys required by endpoint mappings.
 * @returns Flat context keys found in sessionStorage.
 */
export function getStoredSessionContextKeys(keys: string[]): ClientContextKeys {
  if (typeof window === 'undefined' || !window.sessionStorage) return {};

  if (!Array.isArray(keys) || keys.length === 0) return {};

  return keys.reduce((sessionContextKeys: ClientContextKeys, key: string) => {
    const directValue: string | null = window.sessionStorage.getItem(key);
    const alternateKey: string = key.includes('.')
      ? key.replace(/\./g, ':')
      : key.replace(/:/g, '.');
    const alternateValue: string | null =
      window.sessionStorage.getItem(alternateKey);

    const rawValue: string | null = directValue ?? alternateValue;

    if (rawValue == null) {
      return sessionContextKeys;
    }

    try {
      const parsedValue: unknown = JSON.parse(rawValue);

      if (
        typeof parsedValue === 'string' ||
        typeof parsedValue === 'number' ||
        typeof parsedValue === 'boolean'
      ) {
        sessionContextKeys[key] = parsedValue;
        return sessionContextKeys;
      }

      if (parsedValue && typeof parsedValue === 'object') {
        // Keep structured payloads (e.g. user_account.profile) as JSON strings.
        sessionContextKeys[key] = rawValue;
        return sessionContextKeys;
      }
    } catch {
      sessionContextKeys[key] = rawValue;
      return sessionContextKeys;
    }

    return sessionContextKeys;
  }, {});
}

/**
 * Persists Context Setter keys under the single sessionStorage key.
 *
 * @param clientContextKeys - Flat context keys to store.
 * @returns Nothing.
 */
export function storeContextSetterKeys(
  clientContextKeys: ClientContextKeys
): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  if (Object.keys(clientContextKeys).length === 0) return;

  window.sessionStorage.setItem(
    CONTEXT_SETTER_CONTEXT_STORAGE_KEY,
    JSON.stringify(clientContextKeys)
  );
}

/**
 * Removes stored Context Setter keys from sessionStorage.
 *
 * @returns Nothing.
 */
export function clearStoredContextSetterKeys(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  window.sessionStorage.removeItem(CONTEXT_SETTER_CONTEXT_STORAGE_KEY);
}

/**
 * Clears Context Setter revalidation timestamps and resolved markers from sessionStorage.
 *
 * @returns Nothing.
 */
export function clearRemoteContextRevalidationEntries(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const storageKey: string | null = window.sessionStorage.key(index);

    if (
      storageKey?.startsWith(CONTEXT_SETTER_REVALIDATE_STORAGE_PREFIX) ||
      storageKey?.startsWith(CONTEXT_SETTER_RESOLVED_STORAGE_PREFIX)
    ) {
      window.sessionStorage.removeItem(storageKey);
    }
  }
}

/**
 * Builds the sessionStorage key used to record that a remote Context Setter
 * fetch finished for a URL, including empty or metadata-only payloads.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns Namespaced storage key for resolved status.
 */
function getResolvedStorageKey(contextSetterRemoteUrl: string): string {
  return `${CONTEXT_SETTER_RESOLVED_STORAGE_PREFIX}:${contextSetterRemoteUrl}`;
}

/**
 * Records that the remote Context Setter fetch for this URL has completed.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns Nothing.
 */
export function markRemoteContextResolved(
  contextSetterRemoteUrl: string
): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  if (!contextSetterRemoteUrl) return;

  window.sessionStorage.setItem(
    getResolvedStorageKey(contextSetterRemoteUrl),
    '1'
  );
}

/**
 * Whether a later mount can treat the remote Context Setter fetch as complete.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns `true` when this URL has already resolved in this session.
 */
export function hasRemoteContextResolved(
  contextSetterRemoteUrl: string | undefined
): boolean {
  if (typeof window === 'undefined' || !window.sessionStorage) return false;
  if (!contextSetterRemoteUrl) return false;

  try {
    return (
      window.sessionStorage.getItem(
        getResolvedStorageKey(contextSetterRemoteUrl)
      ) !== null
    );
  } catch {
    return false;
  }
}

/**
 * Dispatches the Context Setter ready event on `window`.
 *
 * @returns Nothing.
 */
export function dispatchContextSetterReadyEvent(): void {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(CONTEXT_SETTER_READY_EVENT));
}

/**
 * Clears cached Context Setter data so the next fetch uses the current session.
 *
 * @returns Nothing.
 */
export function invalidateStoredRemoteContext(): void {
  clearStoredContextSetterKeys();
  clearRemoteContextRevalidationEntries();
  dispatchContextSetterReadyEvent();
}
